import { AppBar, Button, Chip, Container, Toolbar, Tooltip, Typography } from '@material-ui/core'
import { Warning } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { cloneDeep, isEqual, isEqualWith } from 'lodash'
import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { showError } from '../../../actions/misc/misc.actions'
import { updatePet } from '../../../actions/pets/pets.actions'
import { getVets } from '../../../actions/vets/vets.actions'
import PetBehaviorCard from '../../../components/Cards/Pet/PetBehaviorCard'
import PetFacilityMealsCard from '../../../components/Cards/Pet/PetFacilityMealsCard'
import PetHealthCard from '../../../components/Cards/Pet/PetHealthCard'
import PetHomeServicesCard from '../../../components/Cards/Pet/PetHomeServicesCard'
import PetInfoCard from '../../../components/Cards/Pet/PetInfoCard'
import PetKennelingCard from '../../../components/Cards/Pet/PetKennelingCard'
import PetMedicationsCard from '../../../components/Cards/Pet/PetMedicationsCard'
import PetServicesCard from '../../../components/Cards/Pet/PetServicesCard'
import PetVetRecordsCard from '../../../components/Cards/Pet/PetVetRecordsCard'
import PetTrainingCard from '../../../components/Cards/Pet/PetTrainingCard'
import PetStaffNotesCard from '../../../components/Cards/Pet/PetStaffNotesCard'
import GroomingServicesCard from '../../../components/Cards/Pet/GroomingServicesCard'
import LoadingOverlay from '../../../components/Misc/LoadingOverlay'
import { getChipStyles, getDogIssues } from '../../../services/helpers'
import ToolTipItemPet from '../../../components/Pets/ToolTipItemPet'
import GroomingProfileWizard, { ENTRY_POINTS } from '../../../components/Wizards/Grooming/GroomingProfileWizard'
import useSelectedHousehold from '../../../hooks/useSelectedHousehold'
import useSelectedFacility from '../../../hooks/useSelectedFacility'

const useStyles = makeStyles(() => ({
	root: {
		flexGrow: 1,
		paddingTop: '24px',
		paddingLeft: '20px',
		paddingRight: '20px',
		paddingBottom: '75px'
	},
	appBar: {
		top: 'auto',
		bottom: 0,
		paddingRight: '0px !important'
	}
}))

const Pet = () => {
	const classes = useStyles()
	const params = useParams()
	const dispatch = useDispatch()

	const vets = useSelector((state) => state.specific.vets.vets)

	const [pet, setPet] = useState(null)
	const [current, setCurrent] = useState(null)
	const [isPetValid, setIsPetValid] = useState(false)
	const [profileValid, setProfileValid] = useState(false)
	const [medicationsValid, setMedicationsValid] = useState(false)
	const [isSaveShown, setIsSaveShown] = useState(false)
	const [vaccines, setVaccines] = useState([])
	const [currentVaccines, setCurrentVaccines] = useState([])
	const [groomingProfileWizardOpen, setGroomingProfileWizardOpen] = useState(false)

	// hooks
	const { household, loading } = useSelectedHousehold()
	const { facility } = useSelectedFacility()

	const handleGrooomingProfileWizardState = useCallback(() => {
		setGroomingProfileWizardOpen((prev) => !prev)
	}, [setGroomingProfileWizardOpen])

	useEffect(() => {
		if (params.petId && household) {
			let currentPet = household.pets.find((pet) => pet._id === params.petId)
			if (currentPet) {
				if (Array.isArray(currentPet.vaccines)) {
					currentPet.vaccines = currentPet.vaccines.filter((v) => v.type)

					if (Array.isArray(facility.vaccines_required) && facility.vaccines_required.length) {
						currentPet.vaccines = currentPet.vaccines.map((v) => {
							let found = facility.vaccines_required.find((rv) => rv.name === v.type)

							return found ? { ...v, id: found.id } : v
						})
					}
				}

				if (!vets) {
					dispatch(getVets())
				}

				if (vets && vets.length) {
					if (currentPet.vet_id && !currentPet.vet) {
						let vet = vets.find((vet) => vet._id === currentPet.vet_id)

						currentPet = {
							...currentPet,
							vet
						}
					}
				}
				setPet({ ...currentPet })
				setCurrent({ ...currentPet })
			}
		}
	}, [params.petId, household, vets])

	useEffect(() => {
		if (current && pet && pet._id) {
			let petEqual = !isEqualWith(current, pet, (value, other) => {
				let v = cloneDeep(value)
				let o = cloneDeep(other)

				if (v.tags) {
					Object.keys(v.tags).forEach((key) =>
						v.tags[key] ? v.tags[key].sort((a, b) => (a._id > b._id ? 1 : -1)) : null
					)
					Object.keys(o.tags).forEach((key) =>
						o.tags[key] ? o.tags[key].sort((a, b) => (a._id > b._id ? 1 : -1)) : null
					)
				}
				if (v.intolerances) {
					Object.keys(v.intolerances).forEach((key) =>
						v.intolerances[key]
							? v.intolerances[key].sort((a, b) => (a.toUpperCase() > b.toUpperCase() ? 1 : -1))
							: null
					)
					Object.keys(o.intolerances).forEach((key) =>
						o.intolerances[key]
							? o.intolerances[key].sort((a, b) => (a.toUpperCase() > b.toUpperCase() ? 1 : -1))
							: null
					)
				}
				if (v.vaccines) {
					v.vaccines.sort((a, b) => (a.type.toUpperCase() > b.type.toUpperCase() ? 1 : -1))
					o.vaccines.sort((a, b) => (a.type.toUpperCase() > b.type.toUpperCase() ? 1 : -1))
				}

				const medications = v.medications || []

				medications.forEach((medication) =>
					medication.times_of_day
						? medication.times_of_day.sort((a, b) => (a.toUpperCase() > b.toUpperCase() ? 1 : -1))
						: null
				)

				o.medications.forEach((medication) =>
					medication.times_of_day
						? medication.times_of_day.sort((a, b) => (a.toUpperCase() > b.toUpperCase() ? 1 : -1))
						: null
				)

				let equal = isEqual(v, o)

				return equal
			})

			setIsSaveShown(petEqual)
		}
	}, [current, pet])

	const clearChanges = () => {
		setPet(cloneDeep(current))
		setCurrent(cloneDeep(current))
		setVaccines(currentVaccines)
	}

	useEffect(() => {
		if (pet) {
			setProfileValid(isProfileValid())
			setMedicationsValid(areMedicationsValid())
		}
	}, [pet])

	useEffect(() => {
		setIsPetValid(profileValid && medicationsValid)
	}, [profileValid, medicationsValid])

	const savePet = () => {
		if (!isPetValid) {
			if (!profileValid) {
				dispatch(showError('Missing required info fields.'))
			}

			if (!medicationsValid) {
				dispatch(showError('Missing required medication fields.'))
			}
		} else {
			let updates = {
				...pet,
				origin: pet.origin || '',
				personality: pet.personality || '',
				tags: Object.values(pet.tags)
					.filter((tag) => tag)
					.flat()
					.map((tag) => tag._id),
				other_playgroup_types: pet.other_playgroup_types?.length ? pet.other_playgroup_types : [],
				best_fit_playgroup: pet.best_fit_playgroup ? pet.best_fit_playgroup : '',
				alt_playgroups: pet.alt_playgroups ? pet.alt_playgroups : [],
				medications: pet.medications,
				allowed_grooming_services: pet?.allowed_grooming_services || [],
				grooming_addons: pet?.grooming_addons || [],
				meds_to_delete: pet.meds_to_delete,
				vaccines: pet.vaccines,
				night_program_restrictions: pet.night_program_restrictions || [],
				grooming_duration: pet.grooming_duration ? Number(pet.grooming_duration) : null
			}
			if (updates.duration_table && updates.duration_table.toLowerCase() === 'none')
				updates.duration_table = null
			dispatch(updatePet(pet._id, updates))
		}
	}

	const isProfileValid = () => {
		let { name } = pet

		return Boolean(name)
	}

	const areMedicationsValid = () => {
		if (pet.medications && pet.medications.length) {
			return pet.medications.every((medication) => {
				let { name } = medication

				return Boolean(name)
			})
		} else {
			return true
		}
	}

	const renderToolTip = (issues) => {
		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				{issues.map((i, index) => (
					<ToolTipItemPet key={index} message={i.message} color={i.color || null} />
				))}
			</div>
		)
	}

	const renderDogAlert = (pet) => {
		const issues = getDogIssues(pet)
		return (
			<div style={{ marginLeft: '15px' }}>
				{issues && issues.length ? (
					<Tooltip title={renderToolTip(issues)}>
						<Chip
							label={
								<Warning style={{ marginLeft: '10px', fontSize: '15px', fill: 'currentcolor' }} />
							}
							style={
								issues.some((i) => i.type == 'danger')
									? {
										...getChipStyles('rejected'),
										...{ paddingTop: '6px' }
									}
									: {
										...getChipStyles('warning'),
										...{ paddingTop: '6px' }
									}
							}
						/>
					</Tooltip>
				) : null}
			</div>
		)
	}

	return (
		<div>
			{pet && groomingProfileWizardOpen ? (
				<Container className={classes.root} maxWidth="md">
					<GroomingProfileWizard
						open={groomingProfileWizardOpen}
						onClose={handleGrooomingProfileWizardState}
						petDetails={pet}
						from={ENTRY_POINTS.pet}
					/>
				</Container>
			) : null}
			{pet && !groomingProfileWizardOpen ? (
				<Container className={classes.root} maxWidth="md">
					<div>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								position: 'relative',
								paddingLeft: 32,
								marginBottom: 16
							}}>
							<Typography variant="h5" style={{ fontWeight: 800 }}>
								{pet.name}
							</Typography>
							{renderDogAlert(pet)}
						</div>

						<PetInfoCard pet={pet} setPet={setPet} />
						<PetServicesCard pet={pet} setPet={setPet} />
						<PetVetRecordsCard
							pet={pet}
							setPet={setPet}
							vaccines={vaccines}
							setVaccines={setVaccines}
							currentVaccines={currentVaccines}
							setCurrentVaccines={setCurrentVaccines}
						/>
						<PetHealthCard pet={pet} setPet={setPet} />
						<PetMedicationsCard pet={pet} setPet={setPet} />
						<PetFacilityMealsCard
							pet={pet}
							setPet={setPet}
							facilityPermittedMealAdjustments={facility.permitted_meal_adjustments || []}
						/>
						<PetBehaviorCard pet={pet} setPet={setPet} />
						<PetKennelingCard pet={pet} setPet={setPet} />
						<GroomingServicesCard
							pet={pet}
							setPet={setPet}
							household={household}
							setIsSaveShown={setIsSaveShown}
							facility={facility}
							openGroomingProfileWizard={handleGrooomingProfileWizardState}
						/>
						<PetHomeServicesCard pet={pet} setPet={setPet} />
						<PetTrainingCard pet={pet} setPet={setPet} />
						<PetStaffNotesCard pet={pet} setPet={setPet} />
					</div>
					{isSaveShown ? (
						<AppBar position="fixed" color="primary" className={classes.appBar}>
							<Toolbar>
								<Container maxWidth="md">
									<Button
										variant="contained"
										color="secondary"
										disabled={loading}
										style={{ marginRight: '8px' }}
										onClick={() => savePet()}>
										Save Changes
									</Button>
									<Button variant="outlined" color="secondary" onClick={clearChanges}>
										Cancel
									</Button>
								</Container>
							</Toolbar>
						</AppBar>
					) : null}
				</Container>
			) : null}
			{loading && !groomingProfileWizardOpen ? <LoadingOverlay /> : null}
		</div>
	)
}

export default Pet
